import { cloneElement, ReactElement } from "react"
import {
  RiAddLine,
  RiAlertFill,
  RiAlertLine,
  RiArrowDownLine,
  RiArrowDownSLine,
  RiArrowLeftLine,
  RiArrowLeftSLine,
  RiArrowRightDoubleLine,
  RiArrowRightLine,
  RiArrowDropRightFill,
  RiArrowRightSLine,
  RiArrowUpDownFill,
  RiArrowUpLine,
  RiArrowUpSFill,
  RiArrowUpSLine,
  RiCalendar2Fill,
  RiCameraFill,
  RiCheckLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiDownload2Line,
  RiDraftLine,
  RiEqualizer3Line,
  RiEqualizerLine,
  RiEyeFill,
  RiEyeOffFill,
  RiFileChartLine,
  RiFileDownloadLine,
  RiFileList3Line,
  RiFilter3Line,
  RiFocus3Line,
  RiFullscreenLine,
  RiGroupLine,
  RiHome5Line,
  RiInformationFill,
  RiInformationLine,
  RiLineChartLine,
  RiLink,
  RiLinkUnlinkM,
  RiListCheck,
  RiMapPinLine,
  RiMessage2Line,
  RiMore2Fill,
  RiMoreFill,
  RiPencilLine,
  RiQuestionLine,
  RiRefreshLine,
  RiSearchLine,
  RiSettings4Line,
  RiShareBoxFill,
  RiSubtractLine,
  RiTimeLine,
  RiUpload2Line,
  RiQuestionMark,
  RiImageAddLine,
  RiCameraLine,
  RiTeamLine,
  RiUserVoiceLine,
  RiUserLine,
  RiCheckboxCircleFill,
  RiErrorWarningFill,
  RiMoreLine,
  RiBarChartHorizontalFill,
  RiMenuLine,
  RiCircleFill,
  RiCropLine,
  RiTableLine,
  RiZoomInLine,
  RiZoomOutLine,
  RiArrowGoBackFill,
  RiScissorsCutFill,
  RiResetLeftLine,
  RiDivideLine,
} from "@remixicon/react"

import { CustomIconType, IconProps, RemixIconType } from "@/types/components/icon"
import { colors } from "@/components/colors"
import {
  Calendar,
  NoProfile,
  NoTeam,
  Match,
  ListDrag,
  UnreadDot,
  Switch,
  Minimize,
  Rotate,
  Menu,
  Resize,
  Activity,
  NotiOff,
  NotiOn,
  CheckCircle,
  LayoutGrid,
  Calendar2,
} from "./svg"

// ⚠️ Color caveat: entries below that hardcode `color={...}` set the SVG color
// INLINE, which beats any parent `svg { color: <token> }` CSS cascade — the icon
// will NOT follow its container's color token. `Icon` clones the element and
// spreads incoming props, so you CAN override per-usage: `<Icon type="arrow-down"
// color="currentColor" />`. But when an icon must inherit the parent's color
// (e.g. a token-driven button), prefer importing the `@remixicon/react` component
// directly instead of routing through `Icon` (see ActivityHeader's dropdown,
// which uses <RiArrowDropDownFill/> so the button's `svg{color}` applies). — WEB-2137
const IconComponents = {
  "no-profile": <NoProfile width={42} height={42} />,
  "no-team": <NoTeam width={42} height={42} />,
  match: <Match width={18} height={22} />,
  "list-drag": <ListDrag width={8} height={16} />,
  "unread-dot": <UnreadDot width={5} height={20} />,
  switch: <Switch width={20} height={20} />,
  minimize: <Minimize width={20} height={20} />,
  rotate: <Rotate width={20} height={20} />,
  menu: <Menu width={20} height={20} />,
  resize: <Resize width={20} height={20} />,
  activity: <Activity color={colors.neutral?.black} />,
  "noti-off": <NotiOff />,
  "noti-on": <NotiOn />,
  "check-circle": <CheckCircle />,
  "layout-grid": <LayoutGrid />,

  home: <RiHome5Line color={colors.neutral?.black} />,
  "line-chart": <RiLineChartLine color={colors.neutral?.black} />,
  "file-chart": <RiFileChartLine color={colors.neutral?.black} />,
  "file-download": <RiFileDownloadLine color={colors.neutral?.black} />,
  settings: <RiSettings4Line color={colors.neutral?.black} />,
  "arrow-down": <RiArrowDownSLine color={colors.neutral?.grey[1000]} />,
  "arrow-up": <RiArrowUpSLine color={colors.neutral?.grey[1000]} />,
  "arrow-right": <RiArrowRightSLine />,
  "arrow-left": <RiArrowLeftSLine />,
  "arrow-line-down": <RiArrowDownLine size={16} />,
  "arrow-line-up": <RiArrowUpLine size={16} />,
  "arrow-line-right": <RiArrowRightLine size={16} />,
  "arrow-line-left": <RiArrowLeftLine size={16} />,
  "arrow-go-back-fill": <RiArrowGoBackFill size={16} />,
  refresh: <RiRefreshLine size={16} />,
  link: <RiLink size={16} />,
  unlink: <RiLinkUnlinkM size={16} />,
  "information-fill": <RiInformationFill size={16} />,
  check: <RiCheckLine size={16} color={colors.primary?.[500]} />,
  "check-fill": <RiCheckboxCircleFill />,
  upload: <RiUpload2Line size={16} />,
  alert: <RiAlertLine size={16} />,
  "alert-fill": <RiAlertFill size={16} />,
  "arrow-up-down": <RiArrowUpDownFill size={16} />,
  camera: <RiCameraFill size={16} />,
  "camera-line": <RiCameraLine size={16} />,
  "delete-bin": <RiDeleteBinLine color={colors.red?.[500]} size={16} />,
  "share-box": <RiShareBoxFill size={16} />,
  "arrow-up-s": <RiArrowUpSFill size={16} />,
  close: <RiCloseLine />,
  search: <RiSearchLine color={colors.neutral?.grey[400]} />,
  subtract: <RiSubtractLine />,
  add: <RiAddLine />,
  time: <RiTimeLine />,
  calendar: <Calendar width={25} height={24} />,
  calendar2: <Calendar2 width={25} height={24} />,
  "calendar-fill": <RiCalendar2Fill />,
  "eye-off": <RiEyeOffFill color={colors.neutral?.grey[400]} />,
  eye: <RiEyeFill color={colors.neutral?.grey[400]} />,
  "map-pin": <RiMapPinLine color={colors.neutral?.grey[500]} />,
  group: <RiGroupLine color={colors.neutral?.grey[500]} />,
  draft: <RiDraftLine color={colors.neutral?.grey[500]} />,
  pencil: <RiPencilLine color={colors.neutral?.grey[500]} />,
  "list-check": <RiListCheck color={colors.neutral?.grey[500]} />,
  question: <RiQuestionLine />,
  information: <RiInformationLine />,
  download: <RiDownload2Line />,
  fullscreen: <RiFullscreenLine />,
  message: <RiMessage2Line />,
  focus: <RiFocus3Line />,
  "file-list": <RiFileList3Line />,
  filter: <RiFilter3Line />,
  equalizer: <RiEqualizer3Line />,
  "equalizer-line": <RiEqualizerLine />,
  "arrow-right-double": <RiArrowRightDoubleLine />,
  more: <RiMoreFill color={colors.neutral?.grey[400]} />,
  "more-vertical": <RiMore2Fill color={colors.neutral?.grey[400]} />,
  "question-mark": <RiQuestionMark />,
  "image-add": <RiImageAddLine />,
  "team-line": <RiTeamLine />,
  "user-voice-line": <RiUserVoiceLine />,
  "user-line": <RiUserLine />,
  "warning-fill": <RiErrorWarningFill />,
  "more-line": <RiMoreLine />,
  "arrow-drop-right-fill": <RiArrowDropRightFill />,
  "bar-chart-horizontal-fill": <RiBarChartHorizontalFill />,
  "menu-line": <RiMenuLine />,
  "circle-fill": <RiCircleFill />,
  "crop-line": <RiCropLine />,
  "table-line": <RiTableLine />,
  "zoom-in": <RiZoomInLine />,
  "zoom-out": <RiZoomOutLine />,
  "scissor-cut-fill": <RiScissorsCutFill size={16} />,
  reset: <RiResetLeftLine />,
  divide: <RiDivideLine />,
}

const Icon = ({ type, ...props }: IconProps<CustomIconType | RemixIconType>): ReactElement => {
  return cloneElement(IconComponents[type], {
    ...props,
  })
}

export default Icon
